import { Injectable } from '@angular/core';
import { ContextMenu, ContextMenuOptions } from '@bazis/shared/models/context-menu.types';
import { v4 as uuidv4 } from 'uuid';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';

/**
 * **BazisContextMenuService**
 *
 * Manages a stack/list of context menus.
 * Provides helpers to create a menu with merged default options and to dismiss
 * the last or a specific menu instance.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisContextMenuService {
    /**
     * Default options applied to every context menu.
     * Values provided to `create()` override these defaults.
     */
    private _defaultParams: ContextMenuOptions = {
        component: null,
        componentProperties: null,
        keyboardClose: true,
        cssClass: '',
        id: '',
    };

    /**
     * Observable holder for active context menus list.
     * The array order is the creation order (last item is the topmost menu).
     */
    menus = new TemplateObservable([]);

    /**
     * Create and register a new context menu.
     *
     * The menu id is generated with `uuidv4()` when `params.id` is not provided.
     * The created menu exposes `onDidDismiss()` Promise that resolves when `dismiss()` is called.
     *
     * @param params - Partial context menu options to apply.
     * @returns Created `ContextMenu` instance.
     */
    create(params: Partial<ContextMenuOptions>) {
        const contextMenuParams: ContextMenuOptions = {
            ...this._defaultParams,
            ...params,
            id: params.id || uuidv4(),
        };

        const contextMenu: ContextMenu = {
            options: contextMenuParams,
            onDidDismiss: () => {
                return new Promise((resolve) => {
                    contextMenu.dismiss = resolve;
                });
            },
        };

        const menus = this.menus._;
        menus.push(contextMenu);

        this.menus.set(menus);

        return contextMenu;
    }

    /**
     * Dismiss a context menu and remove it from `menus`.
     *
     * If `contextMenu` is not provided, dismisses the last menu in the list.
     * If there is no menu to dismiss, the method returns immediately.
     *
     * @param result - Value passed to the menu `dismiss()` callback. Default: `null`.
     * @param contextMenu - Menu instance to dismiss. Default: `null` (dismiss last).
     * @returns void
     */
    dismiss(result = null, contextMenu = null) {
        // console.log(result);
        let menus = this.menus._;
        if (!contextMenu && menus.length > 0) contextMenu = menus[menus.length - 1];

        if (!contextMenu) return;
        if (contextMenu.dismiss) contextMenu.dismiss(result);
        menus = menus.filter((v) => v.options.id !== contextMenu.options.id);
        this.menus.set(menus);
    }
}
